"use client"
import { useAttributionFormatters } from "@/hooks/use-attribution-formatters"


import { useState, useMemo, useCallback } from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Calendar } from "@/components/ui/calendar"
import {
  getSHOPIFY_PERIOD_OPTIONS,
  MAX_SHOPIFY_HISTORY_YEARS,
  type ShopifyPeriod,
} from "./period-constants"

import type { DateRange } from "react-day-picker"
import { useTranslations } from "next-intl"



export interface ShopifyPeriodSelectorProps {
  period: ShopifyPeriod
  customRange?: { from: string; to: string } | null
  onPeriodChange: (
    period: ShopifyPeriod,
    customRange?: { from: string; to: string },
  ) => void
}



export function ShopifyPeriodSelector({
  period,
  customRange,
  onPeriodChange,
}: ShopifyPeriodSelectorProps) {
  const { formatYmdDateRangeLabel } = useAttributionFormatters()


  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardShopifyPeriodSelector")
  const PRESET_OPTIONS = getSHOPIFY_PERIOD_OPTIONS(useTranslations("ShopifyPeriods")).filter(option => option.value !== "custom")

  const [calendarOpen, setCalendarOpen] = useState(false)
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>()

  const today = useMemo(() => new Date(), [])
  const minDate = useMemo(
    () =>
      new Date(
        today.getFullYear() - MAX_SHOPIFY_HISTORY_YEARS,
        today.getMonth(),
        today.getDate(),
      ),
    [today],
  )

  const selectValue = period === "custom" ? "__custom__" : period

  const handlePresetChange = useCallback(
    (value: string) => {
      if (value === "__custom__") return
      onPeriodChange(value as ShopifyPeriod)
    },
    [onPeriodChange],
  )

  const handleApply = useCallback(() => {
    if (!pendingRange?.from || !pendingRange?.to) return
    const from = format(pendingRange.from, "yyyy-MM-dd")
    const to = format(pendingRange.to, "yyyy-MM-dd")
    onPeriodChange("custom", { from, to })
    setCalendarOpen(false)
    setPendingRange(undefined)
  }, [pendingRange, onPeriodChange])

  const handleCalendarOpenChange = useCallback(
    (open: boolean) => {
      setCalendarOpen(open)
      if (!open) {
        setPendingRange(undefined)
      }
    },
    [],
  )

  const calendarSelected = useMemo<DateRange | undefined>(() => {
    if (pendingRange) return pendingRange
    if (period === "custom" && customRange) {
      return {
        from: new Date(customRange.from + "T00:00:00"),
        to: new Date(customRange.to + "T00:00:00"),
      }
    }
    return undefined
  }, [pendingRange, period, customRange])

  const canApply = pendingRange?.from != null && pendingRange?.to != null

  return (
    <div className="flex items-center gap-1">
      <Select value={selectValue} onValueChange={handlePresetChange}>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <SelectTrigger
                className={
                  period === "custom" && customRange
                    ? "w-[220px] text-xs"
                    : "w-[180px]"
                }
              >
                <SelectValue />
              </SelectTrigger>
            </TooltipTrigger>
            {period === "custom" && customRange && (
              <TooltipContent side="top">
                {formatYmdDateRangeLabel(customRange.from, customRange.to)}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <SelectContent>
          {PRESET_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          {period === "custom" && customRange && (
            <SelectItem value="__custom__">
              {formatYmdDateRangeLabel(customRange.from, customRange.to)}
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      <Popover open={calendarOpen} onOpenChange={handleCalendarOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant={period === "custom" ? "default" : "outline"}
            size="icon"
            className="shrink-0"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            selected={calendarSelected}
            onSelect={setPendingRange}
            disabled={{ before: minDate, after: today }}
            numberOfMonths={2}
            defaultMonth={
              calendarSelected?.from
                ? new Date(
                    calendarSelected.from.getFullYear(),
                    calendarSelected.from.getMonth() - 1,
                  )
                : new Date(today.getFullYear(), today.getMonth() - 1)
            }
          />
          <div className="border-t px-3 py-2 flex items-center justify-between">
            <p key={pendingRange?.from && pendingRange?.to
                ? formatYmdDateRangeLabel(
                    format(pendingRange.from, "yyyy-MM-dd"),
                    format(pendingRange.to, "yyyy-MM-dd"),
                  )
                : pendingRange?.from
                  ? tUi("value0", { value0: String(format(pendingRange.from, "MMM d, yyyy")) })
                  : tUi("selectStartAndEndDates")} className="text-xs text-muted-foreground">
              {pendingRange?.from && pendingRange?.to
                ? formatYmdDateRangeLabel(
                    format(pendingRange.from, "yyyy-MM-dd"),
                    format(pendingRange.to, "yyyy-MM-dd"),
                  )
                : pendingRange?.from
                  ? tUi("value0", { value0: String(format(pendingRange.from, "MMM d, yyyy")) })
                  : tUi("selectStartAndEndDates")}
            </p>
            <Button size="sm" disabled={!canApply} onClick={handleApply}>
              <span key={tUi("apply")}>{tUi("apply")}</span></Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
